import { useCallback, useEffect, useMemo, useState } from "react";
import Sync from "../../services/sync";
import { useParams } from "react-router-dom";
import { useQuery, useHasPermission } from "../../common/Hooks";
import { Button } from "react-bootstrap";
import Icon from "../controls/Icon";
import Toolbar from "../controls/Toolbar";
import { useTranslation } from "react-i18next";
import Utils from "../../utils/utils";
import { TYPES, MODULES } from "../../config/logs.js";
import Dropdown from 'react-bootstrap/Dropdown';

const MODULE_ICONS = {
    Application: 'Info',
    Auth: 'Lock',
    Cache: 'SdCard',
    Database: 'Save',
    Sync: 'Sync',
    UI: 'Laptop'
};

const LogsDetails = () => {
    const [translate] = useTranslation();
    const { id: deploymentId } = useParams();
    const queryParams = useQuery();
    const [logs, setLogs] = useState([]);
    const [filteredLogs, setFilteredLogs] = useState([]);
    const [sortedLogs, setSortedLogs] = useState([]);
    const [typeFilters, setTypeFilters] = useState([...TYPES]);
    const [moduleFilters, setModuleFilters] = useState([...MODULES]);
    const [sortAscending, setSortAscending] = useState(true);
    const [sessionId, setSessionId] = useState("");

    const permissions = useHasPermission({
        domain: Utils.getDomain(),
        deployment: deploymentId,
        type: "sessionLogs"
    });

    useEffect(() => {
        setSessionId(queryParams.get("sessionId"));
    }, [queryParams]);

    const getLogs = useCallback(async (sessionId) => {
        const response = await Sync.getAllLogsInSession(sessionId);

        setLogs(response.data);
    }, []);

    useEffect(() => {
        setLogs([]);
        if (permissions.read && sessionId) {
            getLogs(sessionId);
        }
    }, [permissions.read, sessionId, getLogs]);

    useEffect(() => {
        setFilteredLogs(logs.filter(({ type, module }) => typeFilters.includes(type) && moduleFilters.includes(module)));
    }, [logs, typeFilters, moduleFilters]);

    useEffect(() => {
        setSortedLogs([...filteredLogs].sort((logA, logB) => (Number(logA.createdAt) - Number(logB.createdAt)) * (sortAscending ? 1 : -1)));
    }, [filteredLogs, sortAscending]);

    const logIcon = ({ type, module }, css) => {
        let textCss = `text-${type === "error" || type === "fatal" ? "danger" : type === "info" ? "primary" : type}`;
        return <Icon icon={MODULE_ICONS[module]} className={`${textCss} ${css}`} />;
    };

    const renderLogs = useMemo(() => {
        if (sortedLogs.length > 0) {
            return sortedLogs.map((log, index) => (
                <li className="list-group-item d-lg-flex align-items-start small" key={index}>
                    <div className="d-flex align-items-center">
                        <div className="">{logIcon(log, "d-block")}</div>
                        <div className="badge bg-secondary p-2 mx-3">
                            {Utils.getFormattedDateTime(parseFloat(log.createdAt))}
                        </div>
                    </div>
                    <div className="flex-grow-1">
                        <div className={`d-inline align-middle text-break mt-2 mt-lg-0 ${log.type === "error" || log.type === "fatal" ? "text-danger" : ""}`}>
                            {log.message}
                        </div>
                    </div>
                </li>
            ));
        }

        return (
            <li className="list-group-item small">
                {translate("OTM_NO_LOGS")}
            </li>
        )
    }, [sortedLogs, translate]);

    const checkFilter = useCallback(({ filter, checked, filters }) => {
        if (checked && !filters.includes(filter)) {
            filters.push(filter);
        }
        else if (!checked && filters.includes(filter)) {
            filters.splice(filters.indexOf(filter), 1);
        }

        return [...filters];
    }, []);

    const optionsPopup = useMemo(() => (
        <>
            <Dropdown.Header>
                <div className="d-flex align-items-center">
                    <label className="d-block">{translate('OTM_LOGS_CREATED_AT')}</label>
                    <Button onClick={() => setSortAscending((ascending) => !ascending)} variant="outline-primary" className="ms-auto border-0 p-0">
                        <Icon icon={sortAscending ? 'VerticalAlignTop' : 'VerticalAlignBottom'} className="d-block" />
                    </Button>
                </div>
            </Dropdown.Header>
            <Dropdown.Divider />
            {TYPES.map((value) => (
                <Dropdown.Header key={value}>
                    <div className="d-flex align-items-center">
                        <label className="d-block">{value}</label>
                        <input
                            type="checkbox"
                            className="ms-auto"
                            onChange={(event) => {
                                setTypeFilters((filters) => checkFilter({ filter: value, checked: event.target.checked, filters }));
                            }}
                            checked={typeFilters.includes(value)}
                        />
                    </div>
                </Dropdown.Header>
            ))}
            <Dropdown.Divider />
            {MODULES.map((value) => (
                <Dropdown.Header key={value}>
                    <div className="d-flex align-items-center">
                        <label className="d-block">{value}</label>
                        <input
                            type="checkbox"
                            className="ms-auto"
                            onChange={(event) => {
                                setModuleFilters((filters) => checkFilter({ filter: value, checked: event.target.checked, filters }));
                            }}
                            checked={moduleFilters.includes(value)}
                        />
                    </div>
                </Dropdown.Header>
            ))}
        </>

    ), [translate, sortAscending, checkFilter, typeFilters, moduleFilters]);

    const logDisplayOptions = useMemo(() => (
        <div className="d-flex align-items-center justify-content-end mt-2 mt-lg-0">
            <Dropdown autoClose="outside">
                <Dropdown.Toggle bsPrefix="d-block" variant="outline-primary" className="border-0 ms-1 p-0">
                    <Icon icon="FilterList" className="d-block" />
                </Dropdown.Toggle>
                <Dropdown.Menu>
                    {optionsPopup}
                </Dropdown.Menu>
            </Dropdown>
        </div>
    ), [optionsPopup]);

    const renderToolbar = useMemo(() => {
        return <Toolbar
            components={logDisplayOptions}
            heading={`${translate("OTM_SESSION_LOGS")} - ${sessionId}`} />
    }, [translate, logDisplayOptions, sessionId])

    return sessionId && (
        <div className="card mt-3" style={{ overflowX: 'hidden', overflowY: 'auto' }}>
            <div className="card-header border-bottom">
                {renderToolbar}
            </div>
            <div className="card-body p-0">
                <ul className="list-group list-group-flush">
                    {renderLogs}
                </ul>
            </div>
        </div>
    );
};

export default LogsDetails;